import { A9Service, AbstractDataProviderV2, IResourceParams, ICollectionParams } from 'components/system';
import { typedFastCopy } from 'components/utils';

import type { RsCustomer } from 'common/customers/services/types/RsCustomer';
import type { RsCustomerInfo } from 'common/customers/services/types/RsCustomerInfo';
import type { CustomerAdd } from 'common/customers/services/types/CustomerAdd';
import type { CustomerPatch } from 'common/customers/services/types/CustomerPatch';
import type { RsCustomerImportantFieldsInfo } from 'common/customers/services/types/RsCustomerImportantFieldsInfo';
import { nationalGolfAssociation } from 'common/index/services/types';
import { forceEnum } from 'common/customers/services/types';
import { ICardSearchRequest, ICardSearchResponse } from 'common/devices/card-reader';

@A9Service('customerDataProvider')
export class CustomerDataProvider extends AbstractDataProviderV2<RsCustomer, RsCustomerInfo, CustomerAdd, {}, CustomerPatch> {
    constructor() {
        super();
        this.setApiUrl('middle://rs/customers');
    }

    public add(
        item: CustomerAdd,
        resourceParams: IResourceParams = {},
        cancel: Promise<void> = null,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<RsCustomerInfo> {
        let params = resourceParams;

        if (resourceParams.force) {
            params = typedFastCopy(resourceParams);

            if (resourceParams.force as forceEnum) {
                params.id = 'force';
                item.force = resourceParams.force;
                delete params.force;
            }
        }

        return super.add(item, params, cancel, stealth, doPackItem);
    }

    public getPageNames(
        collectionParams: ICollectionParams = {},
        resourceParams: IResourceParams = {},
        light: boolean = true,
        timeout?: Promise<void>
    ) {
        const url = this.helper.buildUrl(this.apiUrl + '/page-names', resourceParams);
        let firstPage = 0,
            lastPage = 0;
        if (collectionParams.filters) {
            firstPage = collectionParams.filters.firstPage || 0;
            lastPage = collectionParams.filters.lastPage || 0;
            collectionParams.filters.firstPage = firstPage > 0 ? firstPage : 0;
            collectionParams.filters.lastPage = lastPage > 0 ? lastPage : 0;
        }
        return this.service
            .getList<any>(url, this.buildListingRequestParams(collectionParams), timeout)
            .then(list => {
                const result = {};
                if (list.items && list.items.length) {
                    list.items.forEach((item, i) => {
                        result[String(i + firstPage)] = light ? item.lastName + ', ' + item.firstName : item;
                    });
                }
                return result;
            })
            .catch(error => {
                throw this.service.handleError(error, { url: url, method: 'GET', params: collectionParams });
            });
    }

    public updateExs(id, data) {
        return this.getExs(id).then((ids: any) => {
            for (const key in ids)
                if (ids.hasOwnProperty(key) && data.hasOwnProperty(key)) {
                    ids[key] = data[key];
                }
            const url = this.helper.buildUrl(this.apiUrl + '/{id}/exs', { id: id });
            return this.service.updateRequest<any>(url, 'PUT', data).then(result => result.data);
        });
    }

    public updateOccupation(customerId: number, occupationId: number): Promise<RsCustomerInfo> {
        const url = this.helper.buildUrl(this.apiUrl + '/{customerId}', { customerId: customerId });
        return this.service.updateRequest<RsCustomerInfo>(url, 'PATCH', { occupationId: occupationId });
    }

    public getExs(id) {
        const url = this.helper.buildUrl(this.apiUrl + '/{id}/exs', { id: id });
        return this.service.getItem(url).catch(error => {
            throw this.service.handleError(error, { url: url, method: 'GET' });
        });
    }

    public getByExternalId(system: nationalGolfAssociation, externalId: string): Promise<RsCustomerInfo> {
        const url = this.helper.buildUrl(this.apiUrl + '/external/{system}/{externalId}', {
            system: system,
            externalId: externalId
        });
        return this.service.getItem<RsCustomerInfo>(url).catch(error => {
            throw this.service.handleError(error, { url: url, method: 'GET' });
        });
    }

    public getImportantFieldsState(id): Promise<RsCustomerImportantFieldsInfo> {
        const url = this.helper.buildUrl(this.apiUrl + '/{id}/important-fields', { id: id });
        return this.service.getItem<RsCustomerImportantFieldsInfo>(url).catch(error => {
            throw this.service.handleError(error, { url: url, method: 'GET' });
        });
    }

    public setImportantFieldsState(id, ignore: boolean, reason: string, filled: boolean) {
        const url = this.helper.buildUrl(this.apiUrl + '/{id}/important-fields', { id: id });
        return this.service
            .updateRequest<any>(url, 'PUT', {
                importantFieldsFilled: filled,
                importantFieldsIgnore: ignore,
                importantFieldsIgnoreReason: reason
            })
            .then(result => result.data);
    }

    public search(params: ICardSearchRequest): Promise<ICardSearchResponse> {
        const url = this.apiUrl + '/card-search';
        return this.service.getItem<ICardSearchResponse>(url, params);
    }

    public updateActive(customerId: number, active: boolean): Promise<RsCustomerInfo> {
        const url = this.apiUrl + `/${customerId}/active`;
        return this.service.updateRequest<RsCustomerInfo>(url, this.updateMethod, { active: active });
    }
}
